import { useQuery } from '@tanstack/react-query';

import { getGiftCardDetails, getGiftCards } from '@/services/gift-cards';
import type { GiftCardLook } from '@/lib/gift-card-design';

// The backend's public projection, key for key. A MONEY card's price is its
// amount; the class columns are set on a CLASSES card only, which the
// database's kind CHECK guarantees.
export type GiftCard = {
  gift_card_id: string;
  availability: 'PUBLIC' | 'PRIVATE';
  order: number;
  name: string;
  slug: string;
  short_description: string;
  description: string | null;
  price: number;
  currency: string;
  // null = never expires.
  validity_months: number | null;
  // The studio's country runs a sold code to 31 December of the year its
  // months end in (today Germany). An older backend omits it: false.
  validity_to_year_end?: boolean;
  // ACTIVE and PUBLIC classes only.
  class_ids: string[];
  // A money card's PUBLIC class packs and subscriptions (gift cards M1); an
  // older backend sends neither.
  class_pack_ids?: string[];
  subscription_ids?: string[];
  look: GiftCardLook;
} & (
  | {
      kind: 'CLASSES';
      number_of_classes: number;
      // null = no limit.
      credit_expiry_days: number | null;
    }
  | { kind: 'MONEY'; number_of_classes: null; credit_expiry_days: null }
);

export const GIFT_CARDS_QUERY_KEY = ['giftCards'];

export default function useGiftCards() {
  const { data, isLoading, error } = useQuery({
    queryKey: GIFT_CARDS_QUERY_KEY,
    queryFn: getGiftCards,
  });

  return {
    data,
    isLoading,
    error,
  };
}

export function useGiftCard({ slug }: { slug: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['giftCard', slug],
    queryFn: () => getGiftCardDetails({ slug }),
    enabled: !!slug,
  });

  return {
    data,
    isLoading,
    error,
  };
}
